import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Subscription, take} from 'rxjs';
import {ActiveOrder} from '../../../generic/classes/active.order';
import {BarCode} from '../../../generic/classes/barcode';
import {Message} from '../../../generic/classes/message';
import {ShoppingCartLine} from '../../../generic/classes/shoppingcartline';
import {User} from '../../../generic/classes/user';
import {OrderTranslationsComponent} from '../../../generic/component/my/order.translations.component';
import {Logger} from '../../../generic/logging/default-log.service';
import {ActiveOrderService} from '../../../generic/service/active.order.service';
import {ApplicationService} from '../../../generic/service/application.service';

import {DeliveryService} from '../../../generic/service/delivery.service';
import {ShoppingCartService} from '../../../generic/service/shoppingcart.service';
import {UserService} from '../../../generic/service/user.service';
import {BarcodeService} from '../../../service/barcode.service';
import {AddBarcodeRowComponent} from '../../my/order/pending/add.barcode.row.component';
import {NumberFormatPipe} from '../../../generic/pipes/number.format.pipe';
import {CustomDatePipe} from '../../../generic/pipes/custom.date.pipe';
import {ShoppingCartRowComponent} from './shopping.cart.row';
import {SalespersonTelephoneComponent} from '../../../generic/component/my/salesperson.telephone.component';
import {RouterLink} from '@angular/router';

@Component({
	selector: 'shopping-cart',
	templateUrl: 'shopping.cart.component.html',
	styleUrls: [
		'../../../../assets/style/_order-overview.scss',
		'./shopping.cart.component.scss',
		'../../../../assets/style/_layout.scss',
	],
	standalone: true,
	imports: [
		OrderTranslationsComponent,
		RouterLink,
		SalespersonTelephoneComponent,
		FormsModule,
		ReactiveFormsModule,
		ShoppingCartRowComponent,
		AddBarcodeRowComponent,
		CustomDatePipe,
		NumberFormatPipe,
	],
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
	private barcodeService = inject(BarcodeService);
	private shoppingCartService = inject(ShoppingCartService);
	private deliveryService = inject(DeliveryService);
	applicationService = inject(ApplicationService);
	private userService = inject(UserService);
	private logger = inject(Logger);
	private activeOrderService = inject(ActiveOrderService);


	@ViewChild(OrderTranslationsComponent)
	orderTranslationsComponent: OrderTranslationsComponent;

	@ViewChildren('addbarcode')
	public barcodeRows: QueryList<AddBarcodeRowComponent>;

	closingInHours: number;
	closingTimeString: string;
	confirmed: boolean = false;
	confirmAllButtonDisabled = false;
	showBarcode = false;
	sortForm: UntypedFormGroup;
	factorForm: UntypedFormGroup;
	private routerSubscription: Subscription;
	private lastConfirmedActiveOrder: ActiveOrder | null;
	private lastShoppingCartLines: Array<ShoppingCartLine>;

	constructor() {
		const formBuilder = inject(UntypedFormBuilder);

		this.sortForm = formBuilder.group({
			sort: ['barcode']
		});

		this.sortForm.controls.sort.valueChanges.subscribe((value: string) => {
			switch (value) {
				case 'barcode':
					this.shoppingCartLines.sort((a, b) => {
						const barcodeLengthA = a.barcode ? a.barcode.length : 0;
						const barcodeLengthB = b.barcode ? b.barcode.length : 0;
						return barcodeLengthA - barcodeLengthB;
					});
					break;
				case 'quantity':
					this.shoppingCartLines.sort(
						(a, b) =>
							a.quantity / a.qtyByBox - b.quantity / b.qtyByBox,
					);
					break;
				case 'volume':
					this.shoppingCartLines.sort((a, b) => a.volume - b.volume);
					break;
				case 'inserted':
					this.shoppingCartLines.sort(
						(a, b) => a.inserted - b.inserted,
					);
					break;
				case 'description':
					this.shoppingCartLines.sort((a, b) => {
						if (a.bcBarcodeDescription > b.bcBarcodeDescription) {
							return -1;
						}
						if (a.bcBarcodeDescription < b.bcBarcodeDescription) {
							return 1;
						}
						return 0;
					});
					break;
			}
		});

		this.factorForm = formBuilder.group({
			factor: '1.00',
			rounding: '0.01'
		});
	}

	/**
	 *
	 * @returns {Array<ShoppingCartLine>} the shopping cart lines of the current active order.
	 * If there's no active order, return the lastShoppingCartLines, which contains the last confirmed lines.
	 */
	get shoppingCartLines(): Array<ShoppingCartLine> {
		const shoppingCartLines = this.shoppingCartService.activeShoppingCartLines;

		if (!this.activeOrderService.selectedOrder) {
			return this.lastShoppingCartLines;
		}

		return shoppingCartLines;
	}

	/**
	 * @returns {ActiveOrder} the current active order, or the last confirmed one.
	 */
	get activeOrder(): ActiveOrder | null {
		const selectedOrder = this.activeOrderService.selectedOrder;
		return selectedOrder ? selectedOrder : this.lastConfirmedActiveOrder;
	}

	ngOnInit() {
		this.refreshData();
	}

	ngOnDestroy() {
		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}
	}

	getUser(): User {
		return this.userService.getUser();
	}

	/**
	 * Update the barcode sales prices based on the factor and rounding settings
	 *
	 * @param factor
	 * @param rounding
	 */
	updateBarcodeSalesPrices() {
		const rounding = this.factorForm.get('rounding')?.value;
		let factor = this.factorForm.get('factor')?.value;
		factor = factor.replace(',', '.');

		this.barcodeService.updateBarcodeSalesPrice(this.barcodeRows, factor, rounding);
	}

	/**
	 * Calculates the total volume to be displayed on the top of the page
	 *
	 * @returns {number}
	 */
	getTotalVolume(): number {
		let total = 0;

		if (this.shoppingCartLines) {
			this.shoppingCartLines.forEach(s => (total += s.volume));
		}

		return total;
	}

	/**
	 * Calculates the total price to be displayed on the top of the page
	 *
	 * @returns {number}
	 */
	getTotalPrice(): number {
		let total = 0;

		if (this.shoppingCartLines) {
			this.shoppingCartLines.forEach(s => (total += this.getPriceForLine(s)));
		}

		return total;
	}

	confirmBarcodes() {
		this.barcodeService.addBarcodes(
			this.shoppingCartLines,
			this.orderTranslationsComponent.getBarcodeSuccess(),
			this.orderTranslationsComponent.getBarcodeError()
		);
		this.showBarcode = false;
	}

	addBarcode(message: BarCode, line: ShoppingCartLine) {
		line.scBarcodeDescription = message.description;
		line.scBarcode = message.barcode;
		line.scBarcodePrice = message.salesPrice;
	}

	/**
	 * Calculates the total price for the given shopping cart line
	 *
	 * @param shoppingCartLine
	 * @returns {number}
	 */
	getPriceForLine(shoppingCartLine: ShoppingCartLine): number {
		return shoppingCartLine.unitPriceExcResale * shoppingCartLine.quantity;
	}

	isConfirmAllEnabled() {
		return (
			!this.confirmAllButtonDisabled &&
			!this.showBarcode &&
			!this.confirmed &&
			this.shoppingCartLines &&
			this.shoppingCartLines.length > 0
		);
	}

	confirmAll() {
		this.confirmAllButtonDisabled = true;

		this.shoppingCartService.confirmAll(this.activeOrder?.shipmentDate, this.activeOrder?.shipToCode)
			.subscribe(
				confirmed => this.handleConfirmAll(confirmed),
				error => this.logger.error('Error', error)
			);
	}

	private handleConfirmAll(confirmed: boolean) {
		// Hide message if any
		if (this.applicationService.message) {
			this.applicationService.message.isShown = true;
		}

		if (confirmed) {
			this.applicationService.message = new Message(
				this.orderTranslationsComponent.getShoppingCartConfirmed(),
				'info',
				false
			);

			// Before removing the global 'selected order' and 'active shopping cart lines',
			// duplicate the info and add it as local component state.
			// This way the global application state is directly up-to-date
			// and the shopping cart page, still shows all the correct 'confirmed' data.
			this.lastConfirmedActiveOrder = this.activeOrderService.selectedOrder;
			this.lastShoppingCartLines = this.shoppingCartService.activeShoppingCartLines;

			this.activeOrderService.removeSelectedOrder();
			this.activeOrderService.reloadActiveOrders().pipe(take(1)).subscribe();

			this.confirmAllButtonDisabled = false;
			this.confirmed = true;
		} else {
			this.applicationService.message = new Message(
				this.orderTranslationsComponent.getShoppingCartError(),
				'info',
				false
			);
		}
	}

	private refreshData() {
		if (this.activeOrder && this.activeOrder.closingTime !== null) {
			this.closingInHours = this.deliveryService.getClosingInHours(this.activeOrder.closingTime);

			this.closingTimeString = this.deliveryService.getClosingTimeString(
				this.activeOrder.closingTime,
				this.activeOrder.closingHour,
				this.closingInHours,
				this.orderTranslationsComponent.getClosingInDay(),
				this.orderTranslationsComponent.getClosingInDays(),
				this.orderTranslationsComponent.getClosingTomorrow(),
				this.orderTranslationsComponent.getClosed(),
				this.orderTranslationsComponent.getClosedIn()
			);
		}
	}
}
